import { BehaviorSubject } from 'rxjs';
import { NpaId } from '@circe/core';
import {
  NpaTableActionsConfigurationModel, NpaTableCheckboxSelection, NpaTableColumn, NpaTableColumnConfigurationModel,
  NpaTableConfigModel, NpaTableSortSource
} from '../table.types';

export class NpaTableConfiguration<T> {
  public keyColumnParam: string;
  public defaultActionColumnId: string;
  public columns: NpaTableColumnConfigurationModel<T>[];
  public actionsConfigurationModel: NpaTableActionsConfigurationModel<T>;
  public minRowWidth: number;
  public hoverRows: boolean;
  public showHeader: boolean;
  public sortTable: boolean;
  public truncateTexts: boolean;
  public tableHeight$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public columnsWidth: string;
  public minColumnWidth: number;
  public sortSource: NpaTableSortSource<T>;
  public emitOnScroll: boolean;

  private _rows$: BehaviorSubject<NpaTableColumn<T>[][]>;
  private _defaultConfiguration: NpaTableConfigModel<T> = {
    keyColumnParam: 'id',
    defaultActionColumnId: 'id',
    columns: [],
    minColumnWidth: 50,
    showHeader: true,
    sortTable: true,
    hoverRows: false,
    truncateTexts: false,
    emitOnScroll: false
  };

  constructor(rows$: BehaviorSubject<NpaTableColumn<T>[][]>, configModel?: NpaTableConfigModel<T>) {
    this._rows$ = rows$;
    this._processConfiguration(typeof configModel === 'object' ?
      { ...this._defaultConfiguration, ...configModel } : this._defaultConfiguration);
  }

  private _processConfiguration(config: NpaTableConfigModel<T>): void {
    if ('keyColumnParam' in config) {
      this.keyColumnParam = config.keyColumnParam;
    } else {
      throw new Error('You must provide keyColumnParam config in order to get the id from every row.');
    }

    if ('defaultActionColumnId' in config) {
      this.defaultActionColumnId = config.defaultActionColumnId;
    }

    if ('showHeader' in config) {
      this.showHeader = config.showHeader;
    }

    if ('sortTable' in config) {
      this.sortTable = config.sortTable;
    }

    if ('hoverRows' in config) {
      this.hoverRows = config.hoverRows;
    }

    if ('tableHeight' in config) {
      this.tableHeight$.next(config.tableHeight + 'px');
    }

    if ('minColumnWidth' in config) {
      this.minColumnWidth = config.minColumnWidth;
    }

    if ('truncateTexts' in config) {
      this.truncateTexts = config.truncateTexts;
    }

    if ('actionsConfig' in config) {
      this.actionsConfigurationModel = config.actionsConfig;
    }

    if ('emitOnScroll' in config) {
      this.emitOnScroll = config.emitOnScroll;
    }

    if ('columns' in config) {
      this.columns = this._processColumnsConfig(config.columns);
    }

    const _columnsLength: number = this.columns.length;
    this.minRowWidth = (this.minColumnWidth * _columnsLength) + 24 * _columnsLength;
  }

  private _processColumnsConfig(columns: NpaTableColumnConfigurationModel<T>[]): NpaTableColumnConfigurationModel<T>[] {
    const _auxColumns: NpaTableColumnConfigurationModel<T>[] = [];
    for (const column of columns) {
      _auxColumns.push({
        ...column,
        sortable: 'sortable' in column ? column.sortable : true
      });

      // this.checkboxColumnsModel[column.id] = {
      //   checkAll: false,
      //   checkNone: true,
      //   model: false
      // };
    }

    const _columnsWidths: number[] = _auxColumns.map((c: NpaTableColumnConfigurationModel<T>) => c.columnWidth);
    this._calculateColumnsWidth(_columnsWidths);

    // for (const _column of _auxColumns) {
    //   if (_column.type === 'checkbox') {
    //     const _checkboxColumnsModel: NpaTableCheckboxSelection = this.checkboxColumnsModel[_column.id];
    //     this.checkColumnCheckboxes(_column.id);
    //     _checkboxColumnsModel.model = _checkboxColumnsModel.checkAll || !_checkboxColumnsModel.checkNone;
    //   }
    // }

    return _auxColumns;
  }

  private _calculateColumnsWidth(columnsWidths: number[]): void {
    const _showActionsColumn: boolean = this.actionsConfigurationModel && !this.actionsConfigurationModel.hideColumn;
    const _columnsWidths: number[] = _showActionsColumn ?
      [...columnsWidths, this.actionsConfigurationModel.columnWidth] : columnsWidths;
    const totalPercentage: number = 100;
    let gridColumnsWidth: string[] = [];
    let auxPercentage: number = totalPercentage;
    let columnWidthsCount: number = 0;
    let columnsWithoutWidthCount: number = 0;

    for (const _columnWidth of _columnsWidths) {
      if (_columnWidth && (auxPercentage - _columnWidth) >= 0) {
        auxPercentage -= _columnWidth;
        columnWidthsCount++;
      } else {
        columnsWithoutWidthCount++;
      }
    }

    if (auxPercentage === totalPercentage) {
      gridColumnsWidth = new Array(_columnsWidths.length).fill(`${totalPercentage / _columnsWidths.length}%`, 0, _columnsWidths.length);
    } else if (auxPercentage >= 0) {
      gridColumnsWidth = _columnsWidths.map((columnWidth: number) => `${columnWidth ?? (auxPercentage / columnsWithoutWidthCount)}%`);
    }

    if (this.minColumnWidth) {
      gridColumnsWidth = gridColumnsWidth.map((width: string, i: number) => {
        const _minWidth: number = _showActionsColumn && i === gridColumnsWidth.length - 1 ? 16 : this.minColumnWidth;
        return `minmax(${_minWidth}px, ${width})`;
      });
    }

    this.columnsWidth = gridColumnsWidth.join(' ');
  }
}
